import React, { useMemo } from 'react';
import { Node as BoardNode, Edge as BoardEdge } from '../types';
import { format } from 'date-fns';

interface TimelineProps {
    nodes: BoardNode[];
    edges: BoardEdge[];
    onSelectNode: (nodeId: string) => void;
    selectedNodeId: string | null;
}

const Timeline: React.FC<TimelineProps> = ({ nodes, edges, onSelectNode, selectedNodeId }) => {
    // Extract all time-stamped events
    const events = useMemo(() => {
        const list: { id: string, type: 'node' | 'edge', label: string, ts: number, color: string }[] = [];

        nodes.forEach(node => {
            if (node.tsStart) {
                list.push({
                    id: node.id,
                    type: 'node',
                    label: node.label,
                    ts: node.tsStart,
                    color: node.color
                });
            }
        });

        edges.forEach(edge => {
            if (edge.ts) {
                list.push({
                    id: edge.id,
                    type: 'edge',
                    label: edge.label || 'Connection',
                    ts: edge.ts,
                    color: edge.color || '#6B7280'
                });
            }
        });

        return list.sort((a, b) => a.ts - b.ts);
    }, [nodes, edges]);

    // Calculate time range for the ruler
    const timeRange = useMemo(() => {
        if (events.length === 0) return null;
        const start = events[0].ts;
        const end = events[events.length - 1].ts;
        const duration = Math.max(end - start, 1000 * 60 * 60 * 24 * 7); // Min 1 week range
        return { start, end, duration };
    }, [events]);

    // Generate ruler ticks (Hierarchical 3-tier system)
    const ticks = useMemo(() => {
        if (!timeRange) return [];
        const result: { ts: number, label: string, tier: 1 | 2 | 3 }[] = [];

        const days = timeRange.duration / (1000 * 60 * 60 * 24);
        const hours = timeRange.duration / (1000 * 60 * 60);

        let majorInterval: number;
        let medInterval: number;
        let minorInterval: number;

        if (days > 730) {
            majorInterval = 365 * 24 * 60 * 60 * 1000;
            medInterval = 90 * 24 * 60 * 60 * 1000;
            minorInterval = 30 * 24 * 60 * 60 * 1000;
        } else if (days > 120) {
            majorInterval = 30 * 24 * 60 * 60 * 1000;
            medInterval = 7 * 24 * 60 * 60 * 1000;
            minorInterval = 1 * 24 * 60 * 60 * 1000;
        } else if (days > 14) {
            majorInterval = 7 * 24 * 60 * 60 * 1000;
            medInterval = 1 * 24 * 60 * 60 * 1000;
            minorInterval = 6 * 60 * 60 * 1000;
        } else if (hours > 24) {
            majorInterval = 24 * 60 * 60 * 1000;
            medInterval = 6 * 60 * 60 * 1000;
            minorInterval = 1 * 60 * 60 * 1000;
        } else {
            majorInterval = 60 * 60 * 1000;
            medInterval = 15 * 60 * 1000;
            minorInterval = 1 * 60 * 1000;
        }

        const startTS = Math.floor(timeRange.start / minorInterval) * minorInterval;
        const endTS = timeRange.end + (timeRange.duration * 0.1);

        for (let ts = startTS; ts <= endTS; ts += minorInterval) {
            let tier: 1 | 2 | 3 = 3;
            if (ts % majorInterval === 0) tier = 1;
            else if (ts % medInterval === 0) tier = 2;

            const date = new Date(ts);
            let label = "";

            if (tier === 1) {
                if (days > 365) label = format(date, 'yyyy');
                else if (days > 30) label = format(date, 'MMM yyyy');
                else label = format(date, 'MMM dd');
            } else if (tier === 2 && days <= 30) {
                label = format(date, 'HH:mm');
            }

            result.push({ ts, label, tier });
            if (result.length > 800) break;
        }

        return result;
    }, [timeRange]);

    const getXPosition = (ts: number) => {
        if (!timeRange) return 0;
        return ((ts - timeRange.start) / (timeRange.duration * 1.1)) * 100;
    };

    if (!timeRange) return null;

    return (
        <div className="w-full h-16 pointer-events-none select-none overflow-hidden relative">
            <div className="absolute inset-x-0 bottom-0 px-10 h-full">
                {/* Ruler Ticks */}
                <div className="absolute inset-x-10 bottom-4 h-8 flex items-end">
                    {ticks.map((tick, i) => {
                        const isMajor = tick.tier === 1;
                        const isMedium = tick.tier === 2;
                        const height = isMajor ? 'h-5' : (isMedium ? 'h-3' : 'h-1.5');
                        const color = isMajor ? 'bg-gray-400 dark:bg-slate-500' : (isMedium ? 'bg-gray-300 dark:bg-slate-600' : 'bg-gray-200 dark:bg-slate-700');

                        return (
                            <div
                                key={i}
                                className="absolute flex flex-col items-center"
                                style={{ left: `${getXPosition(tick.ts)}%` }}
                            >
                                <div className={`w-px ${height} ${color}`} />
                                {tick.label && (
                                    <div className={`
                                        text-gray-400 dark:text-slate-500 mt-1 font-mono whitespace-nowrap
                                        ${isMajor ? 'text-[9px] font-bold' : 'text-[7px]'}
                                    `}>
                                        {tick.label}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Main Ruler Line */}
                <div className="absolute inset-x-10 bottom-4 h-[1px] bg-gray-200 dark:bg-slate-800" />

                {/* Event Pips (Minimal) */}
                {events.map((event) => (
                    <div
                        key={`${event.type}-${event.id}`}
                        className="absolute bottom-[16px] flex flex-col items-center -ml-1.5 pointer-events-auto cursor-pointer"
                        style={{ left: `${getXPosition(event.ts)}%` }}
                        onClick={() => event.type === 'node' && onSelectNode(event.id)}
                        title={`${event.label} (${format(event.ts, 'MMM dd')})`}
                    >
                        <div
                            className={`w-3 h-3 rounded-full border border-white dark:border-slate-900 shadow-sm transition-transform hover:scale-150 ${selectedNodeId === event.id ? 'scale-150 ring-2 ring-blue-500' : ''}`}
                            style={{ backgroundColor: event.color }}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Timeline;
